import { useState } from "react";
import { useForm } from "react-hook-form";

import { ApiError } from "@/api/client";
import { useCreateField, useDeleteField, useFields } from "@/api/hooks";
import type { FieldType } from "@/api/types";
import { Alert, EmptyState, PageHeader, Spinner, TextField } from "@/components/ui";

const TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text" }, { value: "long_text", label: "Long text" },
  { value: "number", label: "Number" }, { value: "boolean", label: "Yes / No" },
  { value: "date", label: "Date" }, { value: "email", label: "Email" }, { value: "url", label: "URL" },
  { value: "phone", label: "Phone" }, { value: "picklist", label: "Picklist" },
  { value: "multi_picklist", label: "Multi-select picklist" },
];

interface Values { label: string; key: string; field_type: FieldType; options: string; required: boolean }
const toKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^(\d)/, "f_$1").slice(0, 64);

const OBJECTS = [{ value: "contact", label: "Contacts" }, { value: "account", label: "Accounts" }, { value: "deal", label: "Deals" }] as const;
type ObjectName = (typeof OBJECTS)[number]["value"];

export function FieldsPage() {
  const [objectType, setObjectType] = useState<ObjectName>("contact");
  const fields = useFields(objectType);
  const create = useCreateField();
  const remove = useDeleteField(objectType);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, watch, reset, setValue, getFieldState } = useForm<Values>({
    defaultValues: { field_type: "text", required: false, options: "" },
  });
  const type = watch("field_type");
  const needsOptions = type === "picklist" || type === "multi_picklist";
  const labelReg = register("label", { required: true });

  const onSubmit = handleSubmit(async (v) => {
    setErrors({}); setError(null);
    try {
      await create.mutateAsync({
        object_type: objectType, label: v.label, key: v.key, field_type: v.field_type, required: v.required,
        options: needsOptions ? v.options.split(",").map((o) => o.trim()).filter(Boolean) : [],
      });
      reset();
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.length) setErrors(e.fieldErrors());
      else setError(e instanceof Error ? e.message : "Could not create field");
    }
  });

  return (
    <>
      <PageHeader title="Custom fields" subtitle="Add your own fields. They appear in forms, detail pages, imports, exports and the API immediately." />
      <div className="mb-4 inline-flex rounded-md border border-line bg-surface p-0.5" role="tablist" aria-label="Object">
        {OBJECTS.map((o) => (
          <button key={o.value} type="button" role="tab" aria-selected={objectType === o.value} onClick={() => setObjectType(o.value)}
            className={`rounded px-3 py-1 text-sm font-medium ${objectType === o.value ? "bg-brand text-white" : "text-muted hover:text-ink"}`}>{o.label}</button>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2" aria-label="Fields">
          {fields.isLoading && <Spinner />}
          {fields.data?.length === 0 && <EmptyState title="No custom fields" body="Add fields such as Industry, Annual volume or GSTIN verified." />}
          {!!fields.data?.length && (
            <div className="card divide-y divide-line">
              {fields.data.map((f) => (
                <div key={f.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">{f.label}{f.required && <span className="ml-2 rounded bg-brand-soft px-1.5 py-0.5 text-xs text-brand">Required</span>}</p>
                    <p className="text-xs text-muted">{f.key} · {TYPES.find((t) => t.value === f.field_type)?.label}{f.options.length ? ` · ${f.options.join(", ")}` : ""}</p>
                  </div>
                  <button type="button" className="btn-ghost text-xs" aria-label={`Delete ${f.label}`}
                    onClick={() => { if (window.confirm(`Delete field “${f.label}”?`)) remove.mutate(f.id); }}>Delete</button>
                </div>
              ))}
            </div>
          )}
        </section>
        <section className="card p-5" aria-label="Add field">
          <h2 className="mb-4 font-semibold">Add a {OBJECTS.find((o) => o.value === objectType)!.label.slice(0, -1).toLowerCase()} field</h2>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            {error && <Alert>{error}</Alert>}
            <TextField label="Label" {...labelReg} error={errors.label}
              onChange={(e) => { void labelReg.onChange(e); if (!getFieldState("key").isDirty) setValue("key", toKey(e.target.value)); }} />
            <TextField label="API key" hint="Lowercase letters, digits and underscores" {...register("key", { required: true })} error={errors.key} />
            <div>
              <label htmlFor="field_type" className="label">Type</label>
              <select id="field_type" className="input" {...register("field_type")}>
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {needsOptions && <TextField label="Options" hint="Comma separated" {...register("options")} error={errors.options} />}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-brand" {...register("required")} /> Required</label>
            <button type="submit" className="btn-primary w-full" disabled={create.isPending}>Add field</button>
          </form>
        </section>
      </div>
    </>
  );
}
